// stores/cache.ts
import { defineStore } from 'pinia'

const LS_PREFIX = 'glouton-cache:'

/** Rayon (km) page prix essence — persistant navigateur via localStorage. */
export const GAS_PRICE_RADIUS_CACHE_KEY = 'GasPricePage-selectedRadius'

/** Dernière allée filtrée — page Meilleurs rabais. */
export const BEST_ITEMS_AISLE_CACHE_KEY = 'BestItemsPage-selectedAisle'

/** Dernière période — page Meilleurs rabais. */
export const BEST_ITEMS_PERIOD_CACHE_KEY = 'BestItemsPage-selectedPeriod'

/** Dernier commerce filtré — page Meilleurs rabais. */
export const BEST_ITEMS_STORE_CACHE_KEY = 'BestItemsPage-selectedStore'

/** Dernière allée filtrée — mode grille de la page circulaire. */
export const FLYER_PAGE_AISLE_CACHE_KEY = 'FlyerPage-selectedAisle'

/** Mode de défilement du lecteur magazine — page circulaire. */
export const FLYER_PAGE_SCROLL_MODE_CACHE_KEY = 'FlyerPage-scrollMode'

/** Mode d'affichage préféré — page circulaire. */
export const FLYER_PAGE_DISPLAY_MODE_CACHE_KEY = 'FlyerPage-displayMode'

/** Affichage des allées — page liste d'épicerie. */
export const SHOPPING_LIST_SHOW_AISLES_CACHE_KEY = 'ShoppingListPage-showAisles'

const CACHE_KEYS_PERSIST_LOCAL = new Set<string>([
  GAS_PRICE_RADIUS_CACHE_KEY,
  BEST_ITEMS_AISLE_CACHE_KEY,
  BEST_ITEMS_PERIOD_CACHE_KEY,
  BEST_ITEMS_STORE_CACHE_KEY,
  FLYER_PAGE_AISLE_CACHE_KEY,
  FLYER_PAGE_DISPLAY_MODE_CACHE_KEY,
  FLYER_PAGE_SCROLL_MODE_CACHE_KEY,
  SHOPPING_LIST_SHOW_AISLES_CACHE_KEY,
])

function readLocal(key: string): unknown {
  if (!import.meta.client)
    return undefined
  try {
    const raw = localStorage.getItem(LS_PREFIX + key)
    if (raw == null)
      return undefined
    return JSON.parse(raw) as unknown
  }
  catch {
    return undefined
  }
}

function writeLocal(key: string, data: unknown) {
  if (!import.meta.client || !CACHE_KEYS_PERSIST_LOCAL.has(key))
    return
  try {
    localStorage.setItem(LS_PREFIX + key, JSON.stringify(data))
  }
  catch {
    // quota, mode privé, etc.
  }
}

function removeLocal(key: string) {
  if (!import.meta.client || !CACHE_KEYS_PERSIST_LOCAL.has(key))
    return
  try {
    localStorage.removeItem(LS_PREFIX + key)
  }
  catch {
    // ignore
  }
}

export const useCacheStore = defineStore('cache', () => {
  const cache = ref<Record<string, any>>({})

  function setCache(key: string, data: any) {
    cache.value[key] = data
    writeLocal(key, data)
  }

  function getCache(key: string) {
    if (Object.prototype.hasOwnProperty.call(cache.value, key))
      return cache.value[key]
    if (CACHE_KEYS_PERSIST_LOCAL.has(key)) {
      const fromLs = readLocal(key)
      if (fromLs !== undefined) {
        cache.value[key] = fromLs
        return fromLs
      }
    }
    return undefined
  }

  function clearCache(key: string) {
    delete cache.value[key]
    removeLocal(key)
  }

  return { cache, setCache, getCache, clearCache }
})
